import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";

import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import {
  useMutationWithError,
  WhooshMutationError,
} from "../../../../../../utils/hooks/useMutationWithError";
import { bookingSummaryAnalyticsDataAtom } from "../atoms/analytics/bookingSummaryAnalyticsDataAtom";
import {
  DeleteSlotBookingInput,
  useDeleteSlotBookingMutation,
  useDeleteSlotBookingMutation$data,
} from "./__generated__/useDeleteSlotBookingMutation.graphql";

export const useDeleteSlotBooking = () => {
  const { showToast } = useToast();
  const [commitDeleteSlotBooking, isDeletingSlotBooking] =
    useMutationWithError<useDeleteSlotBookingMutation>(
      DeleteSlotBookingMutationString
    );

  const analyticsData = useAtomValue(bookingSummaryAnalyticsDataAtom);
  const trackEvent = useTrackEvent();

  const deleteSlotBooking = (
    input: DeleteSlotBookingInput,
    successCallback?: () => void
  ) => {
    const handleCommittedCancellation = (
      semanticStyle: "success" | "warning",
      description?: string | null
    ) => {
      trackEvent("BookingDetail.SlotBookingDeleted", analyticsData ?? {});
      successCallback?.();
      showToast({
        title: t`Booking Cancelled`,
        ...(description ? { description } : {}),
        semanticStyle,
      });
    };

    commitDeleteSlotBooking({
      variables: { input },
      onCompletedErrorCustom: (errors: WhooshMutationError[]) => {
        // Both codes mean the deletion already committed and only a post-commit
        // effect (recurring-series bookkeeping, or the refund itself) failed -- see
        // wrap_post_commit_deletion_result/2 and finish_bulk_slot_bookings_deletion's
        // refund_failed_after_commit clause in bookings.ex. Treating this as an
        // ordinary error would leave the patron on a booking that's already gone.
        const committedWarning = errors.find(
          (error) =>
            error.code ===
              "recurring_request_cancellation_post_commit_incomplete" ||
            error.code === "refund_failed_after_commit"
        );

        if (!committedWarning) return errors;

        handleCommittedCancellation("warning", committedWarning.message);
        return errors.filter((error) => error !== committedWarning);
      },
      onCompleted: (response: useDeleteSlotBookingMutation$data) => {
        if (response.responsePayload?.success) {
          handleCommittedCancellation("success");
        }
      },
    });
  };

  return [deleteSlotBooking, isDeletingSlotBooking] as const;
};

export const DeleteSlotBookingMutationString = graphql`
  mutation useDeleteSlotBookingMutation($input: DeleteSlotBookingInput!) {
    responsePayload: deleteSlotBooking(input: $input) {
      errors {
        code

        message
        field
      }
      success
      startSlot {
        ... on TimeSlot {
          id
          bookings {
            edges {
              node {
                id
                isDeleted
              }
            }
          }
        }
      }
    }
  }
`;
